import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, ArrowRight, BadgeCheck, Gamepad2 } from 'lucide-react';
import { Button } from './ui/button';

const STORE_URL = 'https://loja.fernandotech.com.br/';
const SITE = 'https://fernandotech.com.br';

const CATALOG = [
  { name: 'Peças e componentes para PC e notebook', desc: 'SSD, memória RAM, fontes, placas e upgrades com instalação pela Fernando Tech.' },
  { name: 'Acessórios e periféricos', desc: 'Teclados, mouses, headsets, webcams, cabos e adaptadores.' },
  { name: 'Câmeras e alarmes Intelbras', desc: 'Kits de CFTV, câmeras IP, centrais de alarme e sensores Intelbras.' },
  { name: 'Licenças de software', desc: 'Windows, Office, antivírus e SHOficina ERP com ativação assistida.' },
];

const OnlineStore = () => {
  useEffect(() => {
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'OnlineStore',
      '@id': `${STORE_URL}#store`,
      name: 'Loja Fernando Tech',
      url: STORE_URL,
      image: `${SITE}/fernando-tech-logo.png`,
      description: 'Loja virtual da Fernando Tech: peças, acessórios, câmeras Intelbras e licenças de software com suporte técnico de quem entende de TI em Ribeirão Preto.',
      telephone: '+55-16-99137-8312',
      priceRange: 'R$ 50 – R$ 5.000',
      currenciesAccepted: 'BRL',
      paymentAccepted: 'PIX, Cartão de crédito, Boleto',
      parentOrganization: { '@id': `${SITE}/#organization-extended` },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Catálogo Loja Fernando Tech',
        itemListElement: CATALOG.map(c => ({
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceCurrency: 'BRL',
          url: STORE_URL,
          itemOffered: { '@type': 'Product', name: c.name, description: c.desc },
        })),
      },
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = 'ld-online-store';
    s.text = JSON.stringify(ld);
    document.getElementById('ld-online-store')?.remove();
    document.head.appendChild(s);
    return () => document.getElementById('ld-online-store')?.remove();
  }, []);

  return (
    <section id="loja-virtual" className="py-20 bg-slate-900 overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Loja Virtual Fernando Tech</h2>
          <p className="text-xl text-blue-300 mb-12">Peças, acessórios, câmeras Intelbras e licenças de software — com suporte técnico incluso</p>
          
          <div className="flex justify-center">
            <a
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
              data-testid="online-store-link"
            >
              <div className="relative">
                <div className="w-48 h-48 md:w-64 md:h-64 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-2xl shadow-blue-500/50 transition-all duration-300 group-hover:scale-110 group-hover:shadow-cyan-500/70">
                  <ShoppingCart size={80} className="text-white md:w-32 md:h-32" strokeWidth={1.5} />
                </div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 animate-ping opacity-20"></div>
              </div>
              
              <Button
                size="lg"
                className="mt-8 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold px-8 sm:px-12 py-6 text-lg shadow-2xl shadow-blue-500/50 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105"
              >
                Acessar Loja Virtual
              </Button>
            </a>
          </div>

          {/* Chamada de ação logo abaixo do botão */}
          <a
            href={STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-2 text-cyan-300 hover:text-white font-semibold text-base sm:text-lg transition-colors"
            data-testid="online-store-cta"
          >
            Compre com quem entende de TI — Ver produtos
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
          
          <p className="mt-4 text-gray-400 text-sm">
            Equipamentos, componentes e soluções em TI com instalação, configuração e garantia da Fernando Tech.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
            <Link to="/loja-daten" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 transition-colors" data-testid="online-store-daten-link">
              <BadgeCheck className="w-4 h-4 text-blue-300" /> Loja Daten
            </Link>
            <Link to="/navegamer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 transition-colors" data-testid="online-store-navegamer-link">
              <Gamepad2 className="w-4 h-4 text-fuchsia-300" /> Navegamer (PC gamer)
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OnlineStore;
